import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import { fetchMessageDirectory, fetchUnreadMessages, fetchMessageThreads, sendMessages, markThreadSeen, setExpiredHandler, clearSessionExpired } from './api';
import usePollWhenVisible from './usePollWhenVisible';

// Flask clears the session when these pages load (they are the login page), so the bell must
// treat them as logged-out: no polling, panel closed, until /api/auth/login succeeds.
const LOGIN_PATHS = new Set(['/', '/index', '/validlogin']);

/**
 * MessagesProvider — app-level state for the global Messages bell.
 *
 * Mount it ONCE. It must sit INSIDE the Router and OUTSIDE <Routes>:
 *   · inside the Router, because this provider calls useLocation() (below) to spot the
 *     login pages — mounted outside, React Router throws "useLocation() may be used only
 *     in the context of a <Router> component";
 *   · outside <Routes>, so the panel, its draft and the unread count survive navigation.
 *
 * App.js is the single place that wires this up — read it there rather than trusting a
 * copy here. <MessageCenter /> renders the modal / dock (a portal) and must be inside
 * this provider. TabBar and ProfileHome call useMessages() for the count and toggle().
 */
const Ctx = createContext(null);
export const useMessages = () => useContext(Ctx);      // null when no provider (TabBar guards for it)

const THREADS_POLL_MS = 20000;

export const MessagesProvider = ({ children }) => {
  const { user } = useUser();
  const { pathname } = useLocation();
  const onLoginPage = LOGIN_PATHS.has(pathname.replace(/\/+$/, '') || '/');
  const loggedIn = !!user?.email && !onLoginPage;
  // Polling runs only while a bell is on screen (TabBar pages) or the panel is open/docked —
  // not on the profile/home page, which has no bell. One source of truth: the bell itself.
  const [panel, setPanel] = useState('closed');            // 'closed' | 'open' | 'docked'
  const [bells, setBells] = useState(0);
  const registerBell = useCallback(() => { setBells((n) => n + 1); return () => setBells((n) => n - 1); }, []);
  const active = loggedIn && (bells > 0 || panel !== 'closed');
  // a 401 means the Flask session is gone even though UserContext still holds a user
  // (login page opened, session expired) — see the unread-poll effect below for what it does.
  const [authFailed, setAuthFailed] = useState(false);
  const is401 = (e) => /\b401\b|Not logged in/i.test(e?.message || '');

  const [view, setView] = useState('inbox');             // 'inbox' | 'thread' | 'compose'
  const [activeKey, setActiveKey] = useState(null);

  // ── data ──
  const [unread, setUnread] = useState(0);
  // How many unread readings have landed since this login. 0 = we haven't heard from the
  // server yet, so `unread` is still the initial 0 rather than a real count — MessageCenter
  // uses the first reading as its baseline so a backlog at login isn't announced as an arrival.
  const [unreadReads, setUnreadReads] = useState(0);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [me, setMe] = useState(null);
  const [canBroadcast, setCanBroadcast] = useState(false);
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [error, setError] = useState('');

  // ── compose draft (persists while navigating) ──
  const [draft, setDraft] = useState({ org: null, tier: 'all', search: '', picked: [], selectAll: false, title: '', text: '', replyKey: null });
  const [threadDrafts, setThreadDrafts] = useState({});   // key → reply text

  // ONE unread request at a time, never two in the same 3 s — the app never sees parallel calls from here
  const inFlightRef = useRef(false);
  const lastPollRef = useRef(0);
  const refreshUnread = useCallback(() => {
    if (!loggedIn || inFlightRef.current || Date.now() - lastPollRef.current < 3000) return Promise.resolve();
    inFlightRef.current = true; lastPollRef.current = Date.now();
    return fetchUnreadMessages()
      .then((r) => { setUnread(r.unread || 0); setUnreadReads((n) => n + 1); })
      .catch((e) => { if (is401(e)) { setAuthFailed(true); setUnread(0); } })
      .finally(() => { inFlightRef.current = false; });
  }, [loggedIn]);

  // ONE threads request at a time as well — the 20 s poll, openThread and the arrival toast
  // can all ask at once, and /api/messages/threads is the slow one. A poller that finds the
  // wire busy just rides along with the request already out; `force` (used after sending)
  // waits for it to land and then reads again, so a new thread is never missed.
  const threadsInFlightRef = useRef(null);
  const refreshThreads = useCallback((quiet = false, force = false) => {
    if (!loggedIn) return Promise.resolve();
    const run = () => {
      if (!quiet) setThreadsLoading(true);
      const p = fetchMessageThreads()
        .then((r) => {
          setThreads(r.threads || []); setMe(r.me || null); setCanBroadcast(!!r.canBroadcast);
          setUnread((r.threads || []).reduce((n, t) => n + (t.unread || 0), 0));
          setUnreadReads((n) => n + 1);
          setThreadsLoaded(true); setAuthFailed(false);
        })
        .catch((e) => { if (is401(e)) setAuthFailed(true); else setError(e.message); })
        .finally(() => { if (threadsInFlightRef.current === p) threadsInFlightRef.current = null; setThreadsLoading(false); });
      threadsInFlightRef.current = p;
      return p;
    };
    if (!threadsInFlightRef.current) return run();
    return force ? threadsInFlightRef.current.then(run) : threadsInFlightRef.current;
  }, [loggedIn]);

  const loadPeople = useCallback((force = false) => {
    if (!loggedIn || (people.length && !force)) return Promise.resolve(people);
    setPeopleLoading(true);
    return fetchMessageDirectory(force)
      .then((r) => { setPeople(r.people || []); setCanBroadcast(!!r.canBroadcast); return r.people || []; })
      .catch((e) => { if (is401(e)) setAuthFailed(true); else setError(e.message); return []; })
      .finally(() => setPeopleLoading(false));
  }, [loggedIn, people]);

  // unread poll — one request when a user lands (login), then usePollWhenVisible below: 30 s while
  // they're doing things with messages, easing to 5 min when idle, nothing while the tab is hidden
  // (one read when it comes back). A 401 STOPS polling completely and closes the panel. Nothing
  // retries: the only way back is a new `user` object (a fresh login), which clears authFailed above.
  useEffect(() => { setAuthFailed(false); setUnreadReads(0); clearSessionExpired(); }, [user]);   // new login → re-baseline

  /* Any 401 anywhere in api.js lands here instead of hard-navigating to a
     route that would clear the session. One handler, app-wide — this context
     owns authFailed and is mounted once. */
  useEffect(() => { setExpiredHandler(() => setAuthFailed(true)); }, []);
  useEffect(() => {
    if (!loggedIn) { setUnread(0); setUnreadReads(0); return undefined; }
    if (!active || authFailed) return undefined;             // no bell on this page → nothing to poll for
    refreshUnread();
    return undefined;
  }, [user, loggedIn, active, authFailed, refreshUnread]);
  // opening the panel, a thread, or sending = "someone is active here" → fast cadence
  const pollBump = usePollWhenVisible(refreshUnread, { enabled: loggedIn && active && !authFailed });

  // a 401 closes the panel so a logged-out tab never shows a stale inbox
  useEffect(() => { if (authFailed) setPanel('closed'); }, [authFailed]);

  // threads poll — only while the panel is open
  useEffect(() => {
    if (panel !== 'open' || !loggedIn || authFailed) return undefined;
    refreshThreads(threadsLoaded);
    const id = setInterval(() => refreshThreads(true), THREADS_POLL_MS);
    return () => clearInterval(id);
  }, [panel, loggedIn, authFailed]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── actions ──
  const open = useCallback(() => { pollBump(); setPanel('open'); }, [pollBump]);
  const close = useCallback(() => setPanel('closed'), []);
  const dock = useCallback(() => setPanel('docked'), []);
  // the bell: open → minimise; minimised → reopen where you were; closed → open on the INBOX (the
  // list of conversations), never inside whichever thread was read last
  const toggle = useCallback(() => {
    pollBump();
    if (panel === 'open') { setPanel('docked'); return; }
    if (panel === 'closed') { setView('inbox'); setActiveKey(null); }
    setPanel('open');
  }, [panel, pollBump]);

  const openThread = useCallback(async (key) => {
    pollBump();
    setActiveKey(key); setView('thread'); setPanel('open');
    const t = threads.find((x) => x.key === key);
    if (t && t.unread > 0) {
      // optimistic: clear the badge now …
      // fromMe comes from the server, compared on the login RECORD ID — never on email
      setThreads((prev) => prev.map((x) => (x.key !== key ? x : { ...x, unread: 0, messages: x.messages.map((m) => ({ ...m, unread: false, seenByMe: m.fromMe ? m.seenByMe : true })) })));
      setUnread((n) => Math.max(0, n - t.unread));
      // … then add me to Seen By on exactly those rows (by record id) and re-read
      const ids = t.messages.filter((m) => m.unread).map((m) => m.id);
      try {
        const r = await markThreadSeen(key, ids);
        if (r && r.matched === 0) setError(`Couldn't mark as seen — no unread rows matched (${ids.length} sent, session ${r.me || '?'})`);
      } catch (e) { setError(`Couldn't mark as seen: ${e.message}`); }
      refreshThreads(true);
    }
  }, [threads, refreshThreads, pollBump]);

  const startCompose = useCallback((prefill = {}) => {
    setDraft((d) => ({ ...d, ...prefill }));
    setView('compose'); setPanel('open');
    loadPeople();
  }, [loadPeople]);

  const goInbox = useCallback(() => { setView('inbox'); setActiveKey(null); }, []);

  const send = useCallback(async (recipients, text, audience = '', title = '', threadId = '', toEmail = '') => {
    const r = await sendMessages(recipients, text, audience, title, threadId, toEmail);   // throws with server reason
    pollBump();
    await refreshThreads(true, true);        // force: the new thread/message must be in `threads` before we return
    return r;
  }, [refreshThreads, pollBump]);

  // a reply stays in its conversation; the owner of a group thread may aim it at one person
  // `to` is the target person's login RECORD ID (the server also accepts an email —
  // see loginportal.send_messages — but the UI always sends the id). Empty = everyone.
  const reply = useCallback(async (key, text, to = '') => {
    const t = threads.find((x) => x.key === key);
    if (!t) throw new Error('Thread not found');
    return send([], text, '', '', t.threadId || '', t.role === 'owner' ? to : '');
  }, [threads, send]);

  const activeThread = useMemo(() => threads.find((t) => t.key === activeKey) || null, [threads, activeKey]);

  const ctx = {
    panel, view, open, close, dock, toggle, goInbox,
    unread, unreadReads, threads, threadsLoading, threadsLoaded, activeThread, openThread, refreshThreads,
    me, canBroadcast, people, peopleLoading, loadPeople,
    draft, setDraft, threadDrafts, setThreadDrafts, startCompose, send, reply,
    error, setError, loggedIn, registerBell,
    authFailed,          // the bell is the app's session heartbeat — MessageCenter shows the expiry prompt
  };
  return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
};

export default MessagesProvider;